// The OAuth client_id gate, shared by the browser (the /identity/authorize
// return_to) and the login exchange (auth-session), so the two can never
// disagree: if the exchange were stricter it would throw away a stamp the
// browser sent, and if it were looser it would be the weaker of two gates on a
// value that reaches a session claim.
//
// ATTRIBUTION ONLY. The browser chooses this value, and Stytch validates the
// client only later, at oauthAuthorizeStart. It is provenance for audit rows
// and telemetry, never an authorization input.

/** The opaque form: Stytch-issued (connected-app-live-…) or a first-party client we name. */
const OPAQUE_CLIENT_ID = /^[A-Za-z0-9._~:-]{1,200}$/

/**
 * A CIMD client_id, which IS a URL — that is the premise of Client ID Metadata
 * Documents. Claude sends `https://claude.ai/oauth/mcp-oauth-client-metadata`.
 *
 * Validated structurally rather than by widening OPAQUE_CLIENT_ID to allow
 * "/": a charset loose enough for a URL is loose enough to stop being a check.
 * Refuses credentials in the authority, and control characters and whitespace
 * (checked one character at a time, since eslint's no-control-regex refuses
 * the regex form), because this value reaches log lines and a session claim.
 */
function cimdClientId(raw: string): string | undefined {
  if (!/^https:\/\//i.test(raw)) return undefined
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code <= 0x20 || code === 0x7f) return undefined
  }
  try {
    const url = new URL(raw)
    if (url.protocol !== "https:") return undefined
    if (url.hostname.length === 0) return undefined
    if (url.username !== "" || url.password !== "") return undefined
    return raw
  } catch {
    return undefined
  }
}

/** The client_id when it is usable (opaque or CIMD, at most 200 characters), otherwise undefined. */
export function validClientId(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0 || value.length > 200) return undefined
  if (OPAQUE_CLIENT_ID.test(value)) return value
  return cimdClientId(value)
}
